import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';

// --- Helper Functions ---
// Capitalize first letter of each word
const capitalizeName = (name: string): string => {
  // Remove leading/trailing spaces first
  const trimmedName = name.trim();
  // Capitalize
  return trimmedName
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format phone number: (XX) XXXXX-XXXX or (XX) XXXX-XXXX
const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, ''); // Remove non-digits
  const match = cleaned.match(/^(\d{0,2})(\d{0,5})(\d{0,4})$/);
  if (!match) return phone; // Return original if something unexpected happens

  let formatted = '';
  if (match[1]) {
    formatted += `(${match[1]}`;
  }
  if (match[1].length === 2) {
    formatted += ')';
    if (match[2]) {
      formatted += ` ${match[2]}`;
      if (match[2].length === 5 && match[3]) {
        formatted += `-${match[3]}`;
      } else if (match[2].length === 4 && match[3]) {
        formatted += `-${match[3]}`;
      }
    }
  }
  
  // Limit length to avoid exceeding mask
  const maxLength = 15; // (XX) XXXXX-XXXX
  return formatted.slice(0, maxLength);
};
// --- End Helper Functions ---

type ConsultationFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function ConsultationFormModal({ isOpen, onClose }: ConsultationFormModalProps) {
  // Re-add states
  const initialFormData = { name: '', phone: '', email: '', subject: '', description: '' };
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset form on open
      setFormData(initialFormData);
      setErrors({});
      setIsSubmitting(false);
      setSubmissionStatus('idle');
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Re-add handleInputChange with transformations
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'name') {
      // Prevent leading/trailing spaces directly on input, handle capitalization on blur/submit if needed or live
      processedValue = value.trimStart(); 
      // Live capitalization can be jumpy, maybe apply only on blur or use the helper later?
      // For simplicity here, we'll just trim start. Capitalization can be done before submit.
    } else if (name === 'phone') {
      processedValue = formatPhone(value);
    } else if (name === 'subject') {
      processedValue = value.toUpperCase();
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear general form error/success on interaction
    if (submissionStatus !== 'idle') {
      setSubmissionStatus('idle');
    }
  };

  // ADD Handler for blur event, specifically for name capitalization
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'name') {
        const capitalizedName = capitalizeName(value); // Capitalize on blur
        if (capitalizedName !== value) { // Update state only if changed
            setFormData(prev => ({ ...prev, name: capitalizedName }));
        }
    }
    // Can add other onBlur logic here if needed for other fields
  };

  // Re-add validation logic
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    const trimmedName = formData.name.trim(); // Use trimmed name for validation
    if (!trimmedName) newErrors.name = 'Nome é obrigatório.';

    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!formData.phone.trim()) {
        newErrors.phone = 'Telefone é obrigatório.';
    } else if (phoneDigits.length < 10 || phoneDigits.length > 11) {
        newErrors.phone = 'Formato de telefone inválido.';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório.';
    } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      newErrors.email = 'Formato de e-mail inválido.';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Assunto é obrigatório.';
    if (!formData.description.trim()) newErrors.description = 'Descrição é obrigatória.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Re-add handleSubmit with preventDefault and fetch
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Apply final transformations before validation
    const finalName = capitalizeName(formData.name); // Capitalize before validation
    const finalData = { ...formData, name: finalName };
    setFormData(finalData); // Update state to reflect final capitalization for display

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmissionStatus('idle'); // Reset status

    try {
      const response = await fetch("https://formspree.io/f/xvgazdgn", {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        body: JSON.stringify(finalData) // Send final processed data
      });

      if (response.ok) {
        setSubmissionStatus('success');
        setFormData(initialFormData); // Clear form on success
        // Optionally close modal after a delay
        setTimeout(() => {
             if (isOpen) { // Check if still open before closing
                 onClose();
             }
        }, 2500); 
      } else {
        // Try to get error from Formspree response if possible
        try {
            const data = await response.json();
            const formspreeError = data.errors?.map((err: any) => err.message).join(', ') || 'Erro desconhecido do servidor.';
            setErrors({ form: `Falha no envio: ${formspreeError}` });
        } catch { 
             setErrors({ form: 'Falha ao enviar o formulário. Resposta inválida do servidor.'});
        }
        setSubmissionStatus('error');
      }
    } catch (error) {
      console.error("Submission error:", error);
      setErrors({ form: 'Falha ao conectar com o servidor de envio. Verifique sua conexão.'});
      setSubmissionStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    // Backdrop
    <div 
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4 transition-opacity duration-300 ease-in-out overflow-y-auto"
      onClick={onClose} // Close on backdrop click
    >
      {/* Modal Content */} 
      <div
        className="bg-salus-branco w-full max-w-lg rounded-xl shadow-2xl p-6 md:p-8 mx-auto relative flex flex-col gap-4 my-8 animate-fade-in-scale"
        onClick={(e) => e.stopPropagation()} 
      >
        {/* Close Button */} 
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-salus-azul-escuro/70 hover:text-salus-azul-escuro transition-colors disabled:opacity-50"
          aria-label="Fechar formulário"
          disabled={isSubmitting} // Disable close while submitting
        >
          <X size={20} />
        </button>

        {/* Title */} 
        <h2 className="text-xl md:text-2xl font-semibold text-salus-azul-escuro font-heading pr-6">
          Solicitar Consultoria
        </h2>

        {/* Submission Status Messages */} 
        {submissionStatus === 'success' && (
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-100 p-3 rounded-md">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>Solicitação enviada com sucesso! Entraremos em contato em breve.</span>
          </div>
        )}
         {submissionStatus === 'error' && errors.form && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-100 p-3 rounded-md">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{errors.form}</span>
          </div>
        )}

        {/* Form - Now controlled with onSubmit */} 
        <form 
          onSubmit={handleSubmit} // Use internal handler
          noValidate // Disable browser validation in favor of ours
          className="space-y-4"
        >
          {/* Input Fields */} 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-salus-azul-escuro mb-1">Nome <span className="text-red-600">*</span></label>
              <input 
                 type="text" 
                 name="name" 
                 id="name" 
                 placeholder="Nome Completo" // Add placeholder
                 value={formData.name} // Bind value
                 onChange={handleInputChange} // Use handler
                 onBlur={handleBlur} // ADD onBlur handler
                 disabled={isSubmitting} // Add disabled state
                 className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-salus-verde-escuro focus:border-salus-verde-escuro ${errors.name ? 'border-red-500' : 'border-gray-300'}`} 
                 required 
               />
              {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>} 
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-salus-azul-escuro mb-1">Telefone <span className="text-red-600">*</span></label>
              <input 
                 type="tel" 
                 name="phone" 
                 id="phone" 
                 placeholder="(99) 99999-9999" // Add placeholder
                 value={formData.phone} // Bind value
                 onChange={handleInputChange} // Use handler
                 disabled={isSubmitting} // Add disabled state
                 maxLength={15} // Match mask length
                 className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-salus-verde-escuro focus:border-salus-verde-escuro ${errors.phone ? 'border-red-500' : 'border-gray-300'}`} 
                 required 
               />
              {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>} 
            </div>
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-salus-azul-escuro mb-1">E-mail <span className="text-red-600">*</span></label>
            <input 
               type="email" 
               name="email" 
               id="email" 
               placeholder="seu@email.com" // Add placeholder
               value={formData.email} // Bind value
               onChange={handleInputChange} // Use handler
               disabled={isSubmitting} // Add disabled state
               className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-salus-verde-escuro focus:border-salus-verde-escuro ${errors.email ? 'border-red-500' : 'border-gray-300'}`} 
               required 
             />
             {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>} 
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-salus-azul-escuro mb-1">Assunto <span className="text-red-600">*</span></label>
            <input 
               type="text" 
               name="subject" 
               id="subject" 
               placeholder="TREINAMENTO, DOCUMENTAÇÃO, CONSULTORIA ETC" // Add placeholder
               value={formData.subject} // Bind value
               onChange={handleInputChange} // Use handler
               disabled={isSubmitting} // Add disabled state
               className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-salus-verde-escuro focus:border-salus-verde-escuro ${errors.subject ? 'border-red-500' : 'border-gray-300'}`} 
               required 
             />
            {errors.subject && <p className="text-red-600 text-xs mt-1">{errors.subject}</p>}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-salus-azul-escuro mb-1">Descrição <span className="text-red-600">*</span></label>
            <textarea 
               name="description" 
               id="description" 
               rows={4} 
               placeholder="Informe aqui o que você deseja, qual a sua dúvida ou sobre o que gostaria de falar." // Add placeholder
               value={formData.description} // Bind value
               onChange={handleInputChange} // Use handler
               disabled={isSubmitting} // Add disabled state
               className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-salus-verde-escuro focus:border-salus-verde-escuro ${errors.description ? 'border-red-500' : 'border-gray-300'}`} 
               required 
             />
            {errors.description && <p className="text-red-600 text-xs mt-1">{errors.description}</p>}
          </div>

          {/* Submit Button */} 
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isSubmitting || submissionStatus === 'success'} // Disable after success too
              className="px-6 py-2 rounded-lg bg-salus-verde-escuro text-white hover:bg-salus-verde-claro transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]" // Add min-width
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enviando...
                </>
              ) : submissionStatus === 'success' ? (
                  'Enviado!' // Indicate success on button
              ) : (
                "Enviar Solicitação"
              )}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}

export default ConsultationFormModal; 